import { useEffect } from 'react';

interface MetaTag {
  name?: string;
  property?: string;
  content: string;
}

interface PageMetaOptions {
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  viewport?: string;
  charset?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  customMeta?: MetaTag[];
}

export const usePageMeta = (options: PageMetaOptions) => {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // Set title
    if (options.title) {
      document.title = options.title;
    }

    // Helper function to update or create meta tag
    const updateMetaTag = (selector: string, content: string) => {
      let meta = document.querySelector<HTMLMetaElement>(selector);
      if (!meta) {
        meta = document.createElement('meta');
        const [attr, value] = selector.replace('meta[', '').replace(']', '').split('=');
        meta.setAttribute(attr || "", value?.replace(/"/g, '') || "");
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Update standard meta tags
    if (options.description) {
      updateMetaTag('meta[name="description"]', options.description);
    }
    if (options.keywords) {
      updateMetaTag('meta[name="keywords"]', options.keywords);
    }
    if (options.author) {
      updateMetaTag('meta[name="author"]', options.author);
    }
    if (options.viewport) {
      updateMetaTag('meta[name="viewport"]', options.viewport);
    }

    // Update Open Graph meta tags
    if (options.ogTitle) {
      updateMetaTag('meta[property="og:title"]', options.ogTitle);
    }
    if (options.ogDescription) {
      updateMetaTag('meta[property="og:description"]', options.ogDescription);
    }
    if (options.ogImage) {
      updateMetaTag('meta[property="og:image"]', options.ogImage);
    }
    if (options.ogUrl) {
      updateMetaTag('meta[property="og:url"]', options.ogUrl);
    }

    // Update Twitter Card meta tags
    if (options.twitterCard) {
      updateMetaTag('meta[name="twitter:card"]', options.twitterCard);
    }
    if (options.twitterTitle) {
      updateMetaTag('meta[name="twitter:title"]', options.twitterTitle);
    }
    if (options.twitterDescription) {
      updateMetaTag('meta[name="twitter:description"]', options.twitterDescription);
    }
    if (options.twitterImage) {
      updateMetaTag('meta[name="twitter:image"]', options.twitterImage);
    }

    // Handle custom meta tags
    if (options.customMeta) {
      options.customMeta.forEach((tag) => {
        if (tag.name) {
          updateMetaTag(`meta[name="${tag.name}"]`, tag.content);
        } else if (tag.property) {
          updateMetaTag(`meta[property="${tag.property}"]`, tag.content);
        }
      });
    }

    // Cleanup function to optionally reset meta tags
    return () => {
      // Optional: You can implement cleanup logic here if needed
    };
  }, [options]);
}; 